// Contextual actions for the current external resource. Every action ORCHESTRATES an
// owning domain (work, conversations, Dana) — it never owns business rules. Actions
// appear based on capability + permission + the presence of a Work Context.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ExternalLink, Link2, Bookmark, Sparkles, Paperclip, MessageSquarePlus } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { useHasPermission, useHasAnyPermission } from '@/rbac/useRbac';
import { getApiErrorMessage } from '@/lib/apiError';
import { openDana } from '@/lib/dana';
import { openExternal, CAPABILITY_IS_OPENABLE } from '../lib/urlPolicy';
import { attachLinkToWorkItem, createBookmark } from '@/lib/webApi';
import type { UrlClassification, WorkContext } from '../lib/types';

interface Props {
  resource: UrlClassification;
  title: string;
  workContext?: WorkContext | null;
  onSendToConversation: () => void;
}

export const WebActionBar: React.FC<Props> = ({ resource, title, workContext, onSendToConversation }) => {
  const canManageBookmarks = useHasAnyPermission(['browser:bookmarks.manage', 'browser:policy.manage', 'tenant:update:own']);
  const canDana = useHasPermission('browser:dana.use');
  const canAttachWork = useHasPermission('work:write');
  const [busy, setBusy] = useState(false);
  const openable = CAPABILITY_IS_OPENABLE(resource.capability);
  const isWorkCtx = workContext && (workContext.type === 'work_item' || workContext.type === 'assignment');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(resource.url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const askDana = () => {
    const ctx = workContext ? ` (in the context of ${workContext.type.replace('_', ' ')} "${workContext.title ?? workContext.id}")` : '';
    openDana(`About this page ${resource.url}${ctx}: `);
  };

  const bookmark = async () => {
    setBusy(true);
    try {
      await createBookmark({ name: title || resource.domain || resource.url, url: resource.url, category: 'Other' });
      toast.success('Added to company bookmarks');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not add bookmark'));
    } finally {
      setBusy(false);
    }
  };

  const attachToWork = async () => {
    if (!isWorkCtx) return;
    setBusy(true);
    try {
      await attachLinkToWorkItem(workContext!.id, title || resource.domain || resource.url, resource.url);
      toast.success('Attached to work');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not attach to work'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {openable && (
        <Button size="sm" onClick={() => openExternal(resource.url)}>
          <ExternalLink className="mr-1.5 h-4 w-4" /> Open externally
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={copy}>
        <Link2 className="mr-1.5 h-4 w-4" /> Copy link
      </Button>
      {canDana && (
        <Button size="sm" variant="outline" onClick={askDana}>
          <Sparkles className="mr-1.5 h-4 w-4" /> Ask Dana
        </Button>
      )}
      {isWorkCtx && canAttachWork && (
        <Button size="sm" variant="outline" disabled={busy} onClick={attachToWork}>
          <Paperclip className="mr-1.5 h-4 w-4" /> Attach to Work
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={onSendToConversation}>
        <MessageSquarePlus className="mr-1.5 h-4 w-4" /> Send to Conversation
      </Button>
      {canManageBookmarks && (
        <Button size="sm" variant="outline" disabled={busy} onClick={bookmark}>
          <Bookmark className="mr-1.5 h-4 w-4" /> Bookmark
        </Button>
      )}
    </div>
  );
};
